import Handlebars from 'handlebars';
import { URL } from 'url';

class TemplateError extends Error {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
  }
}

// This function takes a uri and the req.query dict and if the uri contains a template it will
// iterate through all URI parameters looking for parameters that begin with _ and will
// apply those parameters to the URI handlebars template, by removing the leading _.
//
// the URIs are returned from Marketplace and the query parameters are passed in from the
// client page. So this is intended to provide an easy way for the client page to inject
// whatever they want to inject into the URI that will be used for the redirect.
//
// For example, a uri configured in marketplace with a value of http://foo.com/{{bar}}/{{baz}}/...
// could be called against interstitial-client with /redirect/.../?_bar=123&_baz=456 and the
// resulting uri would be http://foo.com/123/456/...
//
// Any errors will result in the unmodified uri being returned.
const parseUriTemplate = (uri, query) => {
  // Don't bother with template processing if there's no template.
  if (!uri.includes('{{')) {
    return uri;
  }

  const parsedUrl = new URL(uri);
  if (!parsedUrl.hostname) {
    // so this is required to not break existing tests
    return uri;
  }

  if (parsedUrl.hostname.includes('{{')) {
    throw new TemplateError(
      `uri-template: uri ${uri} has a template variable in the domain component`
    );
  }

  const template = Handlebars.compile(uri, {
    strict: true,
    preventIndent: true,
  });

  if (!template) {
    throw new TemplateError(
      `uri-template: could not compile ${uri} as template`
    );
  }

  const templateParameters = {};
  Object.entries(query).forEach(([k, v]) => {
    if (k.startsWith('_')) {
      // remove the leading _ and add it to our list of variables
      templateParameters[k.substring(1)] = v;
    }
  });

  try {
    return template(templateParameters);
  } catch (e) {
    // Any exceptions thrown result in the original uri being returned
    throw new TemplateError(
      `uri-template: could not execute template ${uri}: ${e}`
    );
  }
};

export { TemplateError, parseUriTemplate };
